import React, { useState, useEffect } from 'react';
import { 
  MdWarning, 
  MdCheckCircle, 
  MdError,
  MdInfo
} from 'react-icons/md';
import SharedClassBadge from './SharedClassBadge';

/**
 * Subject Schedule Matrix Component
 * Displays a grid where subjects are assigned to periods and days
 * 
 * Structure:
 * - Rows: Subjects
 * - Columns: Periods
 * - Cells: Checkboxes for each day of the week
 */
function SubjectScheduleMatrix({ 
  subjects, 
  periods, 
  weekdays, 
  onSubjectsChange,
  selectedShifts = []
}) {
  const [conflicts, setConflicts] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);

  // The parent orders periods by shift and then period number. Preserve that
  // order so Morning Period 1/2/3 is followed by Afternoon Period 1/2/3.
  const sortedPeriods = React.useMemo(() => {
    if (!periods || periods.length === 0) return [];
    return [...periods];
  }, [periods]);

  // Validate on subjects or periods change (debounced to prevent excessive re-renders)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      validateSchedule();
    }, 100); // Small delay to batch updates
    
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjects, sortedPeriods, weekdays]);

  /**
   * Check if a day is selected for a subject in a specific period
   */
  const isDaySelected = (subject, periodNum, dayId) => {
    const periodDaysKey = `period_${periodNum}_days`;
    return subject[periodDaysKey] && subject[periodDaysKey].includes(dayId);
  };

  /**
   * Toggle day selection for a subject in a period
   */
  const handleDayToggle = (subjectIndex, periodNum, dayId, checked) => {
    // Prevent default scroll behavior
    const updatedSubjects = [...subjects];
    const subject = updatedSubjects[subjectIndex];
    
    if (!subject) return;
    
    const periodDaysKey = `period_${periodNum}_days`;
    
    if (!subject[periodDaysKey]) {
      subject[periodDaysKey] = [];
    }
    
    if (checked) {
      // Add day if not already present
      if (!subject[periodDaysKey].includes(dayId)) {
        subject[periodDaysKey].push(dayId);
      }
    } else {
      // Remove day
      subject[periodDaysKey] = subject[periodDaysKey].filter(id => id !== dayId);
    }
    
    // Sort day IDs to maintain order
    subject[periodDaysKey].sort((a, b) => a - b);
    
    // Store scroll position before update
    const scrollContainer = document.querySelector('main.overflow-y-auto');
    const scrollPosition = scrollContainer?.scrollTop || window.scrollY;
    
    onSubjectsChange(updatedSubjects);
    
    // Restore scroll position after a brief delay to allow DOM update
    setTimeout(() => {
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollPosition;
      } else {
        window.scrollTo(0, scrollPosition);
      }
    }, 0);
  };

  /**
   * Validate that no two subjects are assigned to the same period-day
   */
  const validateNoConflicts = () => {
    const conflictList = [];
    const periodDayMap = {}; // Track assignments: "period_day" -> subject name
    
    subjects.forEach((subject, subjectIdx) => {
      sortedPeriods.forEach((period, periodIdx) => {
        const periodNum = periodIdx + 1;
        const periodDaysKey = `period_${periodNum}_days`;
        const dayIds = subject[periodDaysKey] || [];
        
        dayIds.forEach(dayId => {
          const key = `${periodNum}_${dayId}`;
          const dayName = weekdays.find(d => d.id === dayId)?.name || `Day ${dayId}`;
          
          if (periodDayMap[key]) {
            conflictList.push({
              subject1: periodDayMap[key].subjectName,
              subject2: subject.name || subject.subject_name,
              period: periodNum,
              periodName: period.name,
              day: dayName,
              dayId: dayId,
              key: key
            });
          } else {
            periodDayMap[key] = {
              subjectName: subject.name || subject.subject_name,
              subjectIndex: subjectIdx
            };
          }
        });
      });
    });
    
    setConflicts(conflictList);
    return conflictList.length === 0;
  };

  /**
   * Validate that each subject has at least one period-day selected
   */
  const validateSubjectHasSchedule = () => {
    const errors = [];
    
    subjects.forEach((subject, idx) => {
      let hasSchedule = false;
      
      sortedPeriods.forEach((period, periodIdx) => {
        const periodNum = periodIdx + 1;
        const periodDaysKey = `period_${periodNum}_days`;
        if (subject[periodDaysKey] && subject[periodDaysKey].length > 0) {
          hasSchedule = true;
        }
      });
      
      if (!hasSchedule) {
        errors.push({
          subjectName: subject.name || subject.subject_name,
          subjectIndex: idx
        });
      }
    });
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  /**
   * Run all validations
   */
  const validateSchedule = () => {
    const noConflicts = validateNoConflicts();
    const allHaveSchedule = validateSubjectHasSchedule();
    return noConflicts && allHaveSchedule;
  };

  /**
   * Get validation status for display
   */
  const getValidationStatus = () => {
    const hasConflicts = conflicts.length > 0;
    const hasErrors = validationErrors.length > 0;
    
    if (hasConflicts || hasErrors) {
      return {
        valid: false,
        message: `${conflicts.length} conflict(s), ${validationErrors.length} subject(s) without schedule`,
        type: 'error'
      };
    }
    
    if (subjects.length === 0) {
      return {
        valid: false,
        message: 'No subjects added',
        type: 'info'
      };
    }
    
    return {
      valid: true,
      message: 'Schedule is valid',
      type: 'success'
    };
  };

  const validationStatus = getValidationStatus();
  
  // Only show validation status if there are issues or if schedule is complete
  const shouldShowValidationStatus = validationStatus.type === 'error' || 
                                      validationStatus.type === 'info' || 
                                      (validationStatus.type === 'success' && subjects.length > 0);

  if (periods.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <MdInfo className="text-3xl text-yellow-500 mx-auto mb-2" />
        <p className="text-yellow-800">Please select a shift to load periods</p>
      </div>
    );
  }

  if (weekdays.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <MdInfo className="text-3xl text-yellow-500 mx-auto mb-2" />
        <p className="text-yellow-800">Loading weekdays...</p>
      </div>
    );
  }

  if (subjects.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <MdInfo className="text-3xl text-gray-400 mx-auto mb-2" />
        <p className="text-gray-600">No subjects are assigned to the selected semester and batches.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Validation Status - Only show when there are issues or when schedule is complete */}
      {shouldShowValidationStatus && (
        <div className={`p-4 rounded-lg border ${
          validationStatus.type === 'error' 
            ? 'bg-red-50 border-red-200' 
            : validationStatus.type === 'success'
            ? 'bg-green-50 border-green-200'
            : 'bg-blue-50 border-blue-200'
        }`}>
        <div className="flex items-center space-x-2">
          {validationStatus.type === 'error' ? (
            <MdError className="text-red-500 text-xl" />
          ) : validationStatus.type === 'success' ? (
            <MdCheckCircle className="text-green-500 text-xl" />
          ) : (
            <MdInfo className="text-blue-500 text-xl" />
          )}
          <p className={`font-medium ${
            validationStatus.type === 'error' 
              ? 'text-red-800' 
              : validationStatus.type === 'success'
              ? 'text-green-800'
              : 'text-blue-800'
          }`}>
            {validationStatus.message}
          </p>
        </div>
        </div>
      )}

      {/* Conflicts List */}
      {conflicts.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <MdWarning className="text-red-500" />
            <h4 className="font-semibold text-red-800">Schedule Conflicts</h4>
          </div>
          <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
            {conflicts.map((conflict, idx) => (
              <li key={idx}>
                <strong>{conflict.subject1}</strong> and <strong>{conflict.subject2}</strong> both assigned to{' '}
                <strong>{conflict.periodName}</strong> on <strong>{conflict.day}</strong>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Subjects Without Schedule */}
      {validationErrors.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <MdWarning className="text-yellow-500" />
            <h4 className="font-semibold text-yellow-800">Subjects Without Schedule</h4>
          </div>
          <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
            {validationErrors.map((error, idx) => (
              <li key={idx}>
                <strong>{error.subjectName}</strong> has no period-day assignments
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Matrix Table */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full border-collapse min-w-full">
            <thead>
              <tr className="bg-gradient-to-r from-blue-50 to-blue-100 border-b-2 border-blue-200">
                <th className="p-4 text-left font-semibold text-gray-800 sticky left-0 bg-gradient-to-r from-blue-50 to-blue-100 z-10 border-r border-blue-200">
                  Subject
                </th>
                {periods.map((period, periodIdx) => {
                  // Check which shift this period belongs to
                  const shiftInfo = selectedShifts.find(s => 
                    period.shift_id === s.id || period.shift_name === s.name
                  );
                  
                  return (
                    <th 
                      key={period.id || periodIdx} 
                      className="p-4 text-center font-semibold text-gray-800 border-r border-blue-200 last:border-r-0 min-w-[200px]"
                    >
                      <div className="flex flex-col items-center space-y-1">
                        <span className="font-bold text-blue-700">{period.name || `Period ${periodIdx + 1}`}</span>
                        <span className="text-xs text-gray-600">
                          {period.start_time || period.start} - {period.end_time || period.end}
                        </span>
                        {shiftInfo && selectedShifts.length > 1 && (
                          <span className="text-xs text-blue-600 font-medium">
                            {shiftInfo.name || shiftInfo.code}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
                <th className="p-4 text-left font-semibold text-gray-800">
                  Tutor
                </th>
              </tr>
            </thead>
            <tbody>
              {subjects.map((subject, subjectIdx) => {
                const subjectName = subject.name || subject.subject_name || 'Unnamed Subject';
                const hasConflictsForSubject = conflicts.some(c => 
                  c.subject1 === subjectName || c.subject2 === subjectName
                );
                const hasErrorForSubject = validationErrors.some(e => 
                  e.subjectName === subjectName
                );
                
                return (
                  <tr 
                    key={subject.id || subject.subject_id || subjectIdx}
                    className={`border-b border-gray-200 hover:bg-gray-50 transition ${
                      hasConflictsForSubject ? 'bg-red-50' : hasErrorForSubject ? 'bg-yellow-50' : ''
                    }`}
                  >
                    <td className="p-4 font-medium text-gray-800 sticky left-0 bg-white z-10 border-r border-gray-200">
                      <div className="flex items-center space-x-2">
                        <span>{subjectName}</span>
                        {subject.is_shared && <SharedClassBadge />}
                        {hasConflictsForSubject && (
                          <MdWarning className="text-red-500" title="Has conflicts" />
                        )}
                        {hasErrorForSubject && (
                          <MdError className="text-yellow-500" title="No schedule assigned" />
                        )}
                      </div>
                    </td>
                    {sortedPeriods.map((period, periodIdx) => {
                      const periodNum = periodIdx + 1;
                      const periodDaysKey = `period_${periodNum}_days`;
                      const selectedDays = subject[periodDaysKey] || [];
                      
                      return (
                        <td 
                          key={period.id || periodIdx} 
                          className="p-3 border-r border-gray-200 last:border-r-0"
                        >
                          <div className="flex flex-wrap gap-2 justify-center">
                            {weekdays.map(day => {
                              const isSelected = isDaySelected(subject, periodNum, day.id);
                              const hasConflict = conflicts.some(c => 
                                c.key === `${periodNum}_${day.id}` &&
                                (c.subject1 === subjectName || c.subject2 === subjectName)
                              );
                              
                              return (
                                <label
                                  key={day.id}
                                  className={`inline-flex items-center space-x-1 px-2 py-1 rounded-md cursor-pointer transition ${
                                    isSelected
                                      ? hasConflict
                                        ? 'bg-red-200 text-red-800 border-2 border-red-400'
                                        : 'bg-blue-100 text-blue-800 border-2 border-blue-300'
                                      : 'bg-gray-100 text-gray-600 border-2 border-gray-200 hover:bg-gray-200'
                                  }`}
                                  title={day.name}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => handleDayToggle(subjectIdx, periodNum, day.id, e.target.checked)}
                                    className="sr-only"
                                  />
                                  <span className="text-xs font-medium">
                                    {day.name.substring(0, 3)}
                                  </span>
                                  {isSelected && hasConflict && (
                                    <MdWarning className="text-red-600 text-xs" />
                                  )}
                                </label>
                              );
                            })}
                          </div>
                        </td>
                      );
                    })}
                    <td className="p-4 text-gray-600 text-sm">
                      {subject.tutor_name || subject.tutor || 'Not Assigned'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-800 mb-2">Legend</h4>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-blue-100 border-2 border-blue-300 rounded"></div>
            <span className="text-gray-700">Selected day</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-200 border-2 border-red-400 rounded"></div>
            <span className="text-gray-700">Conflict</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-gray-100 border-2 border-gray-200 rounded"></div>
            <span className="text-gray-700">Available</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubjectScheduleMatrix;




